import { useRef, useState, useEffect } from "react";
import { Camera, Link, Loader2, RotateCcw, UserCog, X } from "lucide-react";
import {
  Button,
  Dialog, DialogContent,
  UserAvatar,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { UserItem } from "@/features/identity-hub/api/identity-hub.api";
import { useUpdateUser } from "@/features/identity-hub/hooks/useIdentityHub";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";

// ─── Constants ────────────────────────────────────────────────────────────────

const DISPLAY_NAME_MAX = 100;
const DISPLAY_NAME_MIN = 2;
const CHAR_COUNTER_THRESHOLD = 80;
const PHOTO_MAX_BYTES = 512 * 1024; // 500 KB

// ─── Helpers ──────────────────────────────────────────────────────────────────

function validateDisplayName(value: string, t: (k: string) => string): string | null {
  if (value.length === 0) return t("iam.editProfile.displayNameRequired");
  if (value.length > DISPLAY_NAME_MAX) return t("iam.editProfile.displayNameTooLong");
  if (value.trim().length === 0) return t("iam.editProfile.displayNameBlank");
  if (value.trim().length < DISPLAY_NAME_MIN) return t("iam.editProfile.displayNameTooShort");
  return null;
}

function isValidUrl(s: string): boolean {
  try { new URL(s); return true; } catch { return false; }
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── EditPersonDialog ─────────────────────────────────────────────────────────

interface EditPersonDialogProps {
  user: UserItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeTenantId: string | null;
}

export function EditPersonDialog({ user, open, onOpenChange, activeTenantId }: EditPersonDialogProps) {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState("");
  const [touched, setTouched] = useState(false);
  // undefined = unchanged, null = remove, string = new url/data-uri
  const [pendingPhotoUrl, setPendingPhotoUrl] = useState<string | null | undefined>(undefined);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const updateUser = useUpdateUser();

  const currentPhotoUrl = user?.photoUrl ?? null;
  const displayedPhotoUrl: string | null =
    pendingPhotoUrl !== undefined ? pendingPhotoUrl : currentPhotoUrl;

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName);
      setTouched(false);
      setPendingPhotoUrl(undefined);
      setShowUrlInput(false);
      setUrlInput("");
      setUrlError(null);
      setFileError(null);
    }
  }, [user, open]);

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setDisplayName(user?.displayName ?? "");
      setTouched(false);
      setPendingPhotoUrl(undefined);
      setShowUrlInput(false);
      setUrlInput("");
      setUrlError(null);
      setFileError(null);
    }
    onOpenChange(v);
  };

  const handleFileSelect = async (file: File) => {
    setFileError(null);
    if (!file.type.startsWith("image/")) {
      setFileError(t("iam.editProfile.photo.fileMustBeImage"));
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setFileError(t("iam.editProfile.photo.fileTooLarge", { size: Math.round(file.size / 1024) }));
      return;
    }
    const dataUrl = await readFileAsDataUrl(file);
    setPendingPhotoUrl(dataUrl);
    setShowUrlInput(false);
  };

  const handleUrlApply = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) { setUrlError(t("iam.editProfile.photo.enterUrlRequired")); return; }
    if (!isValidUrl(trimmed)) { setUrlError(t("iam.editProfile.photo.invalidUrl")); return; }
    setPendingPhotoUrl(trimmed);
    setShowUrlInput(false);
    setUrlInput("");
    setUrlError(null);
  };

  const nameError = touched ? validateDisplayName(displayName, t) : null;
  const nameUnchanged = displayName.trim() === user?.displayName;
  const photoChanged = pendingPhotoUrl !== undefined;
  const hasChanges = !nameUnchanged || photoChanged;

  const canSubmit =
    !!user &&
    !!activeTenantId &&
    hasChanges &&
    !validateDisplayName(displayName, t) &&
    !updateUser.isPending;

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit || !user || !activeTenantId) return;
    const changes: Record<string, unknown> = {};
    if (!nameUnchanged) changes.displayName = displayName.trim();
    if (photoChanged) changes.photoUrl = pendingPhotoUrl ?? null;
    try {
      await updateUser.mutateAsync({ userId: user.id, tenantId: activeTenantId, changes });
      handleOpenChange(false);
    } catch {
      // hook shows toast
    }
  };

  const showCounter = displayName.length >= CHAR_COUNTER_THRESHOLD;
  const nearLimit = displayName.length > DISPLAY_NAME_MAX;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex w-full max-w-[460px] flex-col gap-0 overflow-hidden p-0">
        <DialogIconHeader
          icon={UserCog}
          title={t("iam.editProfile.title")}
          description={t("iam.editProfile.description")}
        />

        <div className="flex flex-col gap-5 px-6 py-5">

          {/* ── Avatar / image upload — hidden until upload is supported ── */}
          {false && (
            <><div className="flex flex-col items-center gap-2">

              {/* Avatar — click or drag to upload */}
              <div
                className={cn(
                  "group/photo relative cursor-pointer rounded-full transition-all",
                  isDragging && "ring-2 ring-primary ring-offset-2",
                )}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const f = e.dataTransfer.files[0];
                  if (f) void handleFileSelect(f);
                }}
                role="button"
                tabIndex={0}
                aria-label={t("iam.editProfile.photo.changeAria")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileInputRef.current?.click(); }
                }}
              >
                <UserAvatar
                  name={user?.displayName ?? ""}
                  src={displayedPhotoUrl ?? undefined}
                  size="xl"
                  className="ring-2 ring-border"
                />

                {/* Hover / drag scrim */}
                <div className={cn(
                  "absolute inset-0 flex flex-col items-center justify-center gap-0.5 rounded-full",
                  "bg-black/50 transition-opacity pointer-events-none",
                  isDragging ? "opacity-100" : "opacity-0 group-hover/photo:opacity-100",
                )}>
                  <Camera className="h-5 w-5 text-white" />
                  <span className="text-[10px] font-medium text-white leading-none">
                    {isDragging ? t("iam.editProfile.photo.drop") : t("iam.editProfile.photo.change")}
                  </span>
                </div>

                {/* Corner badge — always-visible affordance cue */}
                <div className="pointer-events-none absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-background bg-muted shadow-sm">
                  <Camera className="h-2.5 w-2.5 text-muted-foreground" />
                </div>
              </div>

              {/* Hint */}
              <p className="text-[11px] text-muted-foreground">
                {t("iam.editProfile.photo.hint")}
              </p>

              {/* File error */}
              {fileError && (
                <p className="text-[11px] text-destructive">{fileError}</p>
              )}

              {/* Revert / Remove */}
              {(photoChanged || displayedPhotoUrl) && (
                <div className="flex items-center gap-3">
                  {photoChanged && (
                    <button
                      type="button"
                      onClick={() => setPendingPhotoUrl(undefined)}
                      className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <RotateCcw className="h-2.5 w-2.5" />
                      {t("iam.editProfile.photo.revert")}
                    </button>
                  )}
                  {displayedPhotoUrl && (
                    <button
                      type="button"
                      onClick={() => { setPendingPhotoUrl(null); setShowUrlInput(false); }}
                      className="text-[11px] text-destructive/80 transition-colors hover:text-destructive"
                    >
                      {t("iam.editProfile.photo.remove")}
                    </button>
                  )}
                </div>
              )}

              {/* URL input */}
              {showUrlInput ? (
                <div className="flex w-full flex-col gap-1">
                  <div className="flex gap-1.5">
                    <input
                      autoFocus
                      type="url"
                      value={urlInput}
                      onChange={(e) => { setUrlInput(e.target.value); setUrlError(null); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleUrlApply(); }
                        if (e.key === "Escape") { setShowUrlInput(false); setUrlInput(""); setUrlError(null); }
                      }}
                      placeholder={t("iam.editProfile.photo.urlPlaceholder")}
                      className={cn(
                        "h-7 flex-1 rounded-md border bg-background px-2.5 text-[12px] text-foreground outline-none",
                        "placeholder:text-muted-foreground/50 transition-colors focus:ring-1",
                        urlError
                          ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                          : "border-border focus:border-ring focus:ring-ring/40",
                      )}
                    />
                    <Button type="button" size="sm" className="h-7 px-2.5 text-[11px]" onClick={handleUrlApply}>
                      {t("iam.editProfile.photo.apply")}
                    </Button>
                    <button
                      type="button"
                      onClick={() => { setShowUrlInput(false); setUrlInput(""); setUrlError(null); }}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label={t("iam.editProfile.photo.cancelAria")}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {urlError && <p className="text-[11px] text-destructive">{urlError}</p>}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowUrlInput(true)}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Link className="h-2.5 w-2.5" />
                  {t("iam.editProfile.photo.enterUrlInstead")}
                </button>
              )}
            </div>

            <div className="h-px bg-border" /></>
          )}

          {/* ── Display name ─────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground">
                {t("iam.editProfile.displayName")} <span className="text-destructive">*</span>
              </label>
              {showCounter && (
                <span className={cn("text-[11px] tabular-nums", nearLimit ? "text-destructive" : "text-muted-foreground")}>
                  {displayName.length}/{DISPLAY_NAME_MAX}
                </span>
              )}
            </div>
            <input
              type="text"
              placeholder={t("iam.editProfile.displayNamePlaceholder")}
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); setTouched(true); }}
              onBlur={() => setTouched(true)}
              maxLength={DISPLAY_NAME_MAX + 10}
              className={cn(
                "h-9 rounded-md border bg-background px-3 text-sm text-foreground outline-none",
                "placeholder:text-muted-foreground/60 transition-colors focus:ring-1",
                nameError
                  ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                  : "border-border focus:border-ring focus:ring-ring/40",
              )}
            />
            {nameError && <p className="text-[11px] text-destructive">{nameError}</p>}
          </div>

          {/* ── Email (read-only) ─────────────────────────────── */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">{t("iam.editProfile.email")}</label>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <span className="text-xs text-muted-foreground">{t("iam.shared.pressEscToCancel")}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button size="sm" disabled={!canSubmit} onClick={() => void handleSubmit()}>
              {updateUser.isPending
                ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />{t("iam.editProfile.saving")}</>
                : t("iam.editProfile.save")}
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFileSelect(f);
            e.target.value = "";
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
